// Intent: single lesson of the Szkoła finansów — sections, "zadanie na dziś"
// card, sources as tappable links, and a done-button that unlocks the next day.

import { router, useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { SCHOOL_LESSONS } from '@/content/school-lessons';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAppMetaStore } from '@/store/app-meta-store';
import { colors, radius, spacing } from '@/theme/tokens';

export default function SchoolLessonScreen() {
  const { t } = useTranslation();
  const { day } = useLocalSearchParams<{ day: string }>();
  const done = useAppMetaStore((s) => s.schoolDoneDays);
  const markSchoolLesson = useAppMetaStore((s) => s.markSchoolLesson);

  const lesson = SCHOOL_LESSONS.find((l) => l.day === Number(day));
  if (!lesson) return null;
  const isDone = done.includes(lesson.day);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('school.day', { day: lesson.day })} />

        <AppText variant="headlineMd" color={colors.navy}>
          {lesson.title}
        </AppText>
        <AppText variant="bodyLg">{lesson.teaser}</AppText>

        {lesson.sections.map((section, i) => (
          <View key={i} style={{ gap: spacing.xs }}>
            <AppText variant="headlineSm">{section.heading}</AppText>
            <AppText variant="bodyLg">{section.body}</AppText>
          </View>
        ))}

        {/* Zadanie na dziś */}
        <Card style={{ flexDirection: 'row', gap: spacing.sm, backgroundColor: colors.infoBg }}>
          <Ionicons name="checkbox-outline" size={24} color={colors.primary} />
          <View style={{ flex: 1, gap: 2 }}>
            <AppText variant="labelLg" color={colors.navy}>
              {t('school.taskTitle')}
            </AppText>
            <AppText variant="bodyLg">{lesson.task}</AppText>
          </View>
        </Card>

        {/* Sources — book / institution names only, NOT clickable links
            (owner: a dead link looked bad and links rot; a durable title lets
            anyone find the source themselves). */}
        {lesson.sources.length > 0 && (
          <View style={{ gap: spacing.xs }}>
            <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
              {t('school.sources')}
            </AppText>
            {lesson.sources.map((s, i) => (
              <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs, minHeight: 24 }}>
                <Ionicons name="library-outline" size={16} color={colors.outline} style={{ marginTop: 2 }} />
                <AppText variant="bodyMd" style={{ flex: 1 }}>
                  {s}
                </AppText>
              </View>
            ))}
          </View>
        )}

        <View
          style={{
            backgroundColor: colors.card,
            borderRadius: radius.card,
            padding: spacing.md,
          }}
        >
          <AppText variant="labelMd" color={colors.textSecondary}>
            {t('school.disclaimer')}
          </AppText>
        </View>

        {isDone ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs }}>
            <Ionicons name="checkmark-circle" size={20} color={colors.primary} />
            <AppText variant="labelLg" color={colors.navy}>
              {t('school.doneInfo')}
            </AppText>
          </View>
        ) : (
          <PrimaryButton
            label={t('school.markDone')}
            onPress={async () => {
              await markSchoolLesson(lesson.day);
              router.back();
            }}
          />
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
